import {
  Box, FormControl, FormLabel, Input, Text,
} from "@chakra-ui/react"
import React from "react"
import ReactPlacesAutocomplete from "react-places-autocomplete"
import { Control, useController } from "react-hook-form"
import { omit } from "lodash"

interface Props {
  name: string
  control: Control<any>
  label?: string
  placeholder?: string
  onSelect: (results) => void
  variant?: string
  disabled?: boolean
  mb?: number | string
}

const PlacesAutocompleteField = ({
  name, control, label, placeholder, onSelect, variant = "flushed", disabled = false, mb = 4,
}: Props) => {
  const { field } = useController({ name, control })

  const geocode = (_: string, placeId: string) => {
    // @ts-ignore
    const service = new window.google.maps.places.PlacesService(document.createElement("div"))
    service.getDetails({ placeId }, (result: any) => onSelect(addressComponents(result)))
  }

  const addressComponents = ({ name: venueName, address_components: components }: any) => (
    {
      venue: venueName,
      addressLine1: addressComponent(components, "addressLine1"),
      city: addressComponent(components, "city"),
      state: addressComponent(components, "state"),
      zipcode: addressComponent(components, "zipcode"),
      country: addressComponent(components, "country"),
    }
  )

  const addressComponent = (components: any[], componentName: string) => {
    const find = (type) => components.find((c) => c.types.includes(type))
    const componentValue = () => {
      switch (componentName) {
        case "addressLine1":
          return [find("street_number")?.long_name, find("route")?.long_name].filter(Boolean).join(" ")
        case "city":
          return (find("locality")?.long_name ?? find("sublocality")?.long_name)
        case "state":
          return find("administrative_area_level_1")?.short_name
        case "zipcode":
          return find("postal_code")?.short_name
        case "country":
          return find("country")?.long_name
        default:
          return ""
      }
    }

    return componentValue() ?? ""
  }

  // index.html loads Google Maps with a plain script tag. When that script is
  // blocked (corporate firewalls, ad blockers), react-places-autocomplete
  // throws on mount and took the whole settings page with it (WEBAPP-8C).
  // Without Places, fall back to a plain text input for the same field.
  // @ts-ignore
  const placesAvailable = !!window.google?.maps?.places

  return (
    <FormControl mb={mb} display="flex" alignItems="flex-start" flexDirection="row" isDisabled={disabled}>
      {label && <FormLabel w="25%" mb={0} mt={2}>{label}</FormLabel>}
      <Box w="100%" position="relative">
        {!placesAvailable && (
          <Input
            variant={variant}
            bg="white"
            placeholder={placeholder}
            disabled={disabled}
            name={field.name}
            value={field.value || ""}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
          />
        )}
        {placesAvailable && (
        <ReactPlacesAutocomplete
          value={field.value || ""}
          onChange={(value) => field.onChange(value)}
          onSelect={geocode}
        >
          {({
            getInputProps, suggestions, getSuggestionItemProps,
          }) => (
            <>
              <Input
                variant={variant}
                bg="white"
                {...getInputProps({ placeholder, disabled })}
              />
              {suggestions.length > 0 && !disabled && (
                <Box borderWidth={1} mt={2} pos="absolute" zIndex={1000} bg="white" w="100%" borderRadius="md" boxShadow="md" maxH="200px" overflowY="auto">
                  {suggestions.map((suggestion) => {
                    const { placeId } = suggestion
                    const suggestionItemProps = omit(getSuggestionItemProps(suggestion), "key")

                    return (
                      <Box
                        key={placeId}
                        cursor="pointer"
                        _hover={{ bgColor: "gray.100" }}
                        {...suggestionItemProps}
                      >
                        <Text pt={1} px={2}>{suggestion.description}</Text>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </>
          )}
        </ReactPlacesAutocomplete>
        )}
      </Box>
    </FormControl>
  )
}

export default PlacesAutocompleteField
